/**
 * useOnboarding — server-side onboarding state mutation hook.
 *
 * Wraps `PATCH /api/auth/me/onboarding` (accepts `{ step?, completed?,
 * dismiss_hint? }`, returns the full UserResponse). On success the AuthContext
 * user is updated in place via its `setUser` — the single source of truth — so
 * derived state (`isOnboarded`, `dismissedHints`, `onboardingStep`) stays
 * consistent everywhere without a parallel cache.
 *
 * `dismissHint` additionally applies an OPTIMISTIC append to the cached user so
 * the UI reflects a dismissal instantly rather than waiting for the round trip
 * (the `dismiss_hint` append is idempotent server-side, so the optimistic value
 * reconciles cleanly with the response).
 */
import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import type { User } from '@/types/auth';

export interface OnboardingUpdatePayload {
  step?: number;
  completed?: boolean;
  dismiss_hint?: string;
}

async function patchOnboarding(payload: OnboardingUpdatePayload): Promise<User> {
  const response = await apiClient('/api/auth/me/onboarding', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`Failed to update onboarding (${response.status})`);
  }
  return response.json();
}

export interface UseOnboardingResult {
  /** Current onboarding step (0 when unknown). */
  onboardingStep: number;
  /** True once onboarding has been completed (or skipped-to-complete). */
  isOnboarded: boolean;
  /** Server-persisted dismissed hint keys. */
  dismissedHints: string[];
  /** PATCH partial onboarding state; resolves when the cached user is updated. */
  update: (payload: OnboardingUpdatePayload) => Promise<User>;
  /** Optimistically dismiss a hint, then persist it via PATCH. */
  dismissHint: (key: string) => Promise<User>;
  /** True while a PATCH is in flight. */
  isUpdating: boolean;
}

export function useOnboarding(): UseOnboardingResult {
  const { user, setUser } = useAuth();

  const mutation = useMutation({
    mutationFn: patchOnboarding,
    onSuccess: (updatedUser) => {
      // Reflect the authoritative server response into the single user cache.
      setUser(updatedUser);
    },
    onError: (error) => {
      // Many dismissals are fire-and-forget (see useGettingStarted): callers do
      // not await mutateAsync, so without this handler a failed/offline PATCH
      // surfaces as an unhandled promise rejection. Log and swallow here; the
      // optimistic UI state has already been applied. Callers that DO await
      // update() still receive the rejection from mutateAsync — onError is a
      // side effect, not a catch.
      console.warn('Onboarding PATCH failed', error);
    },
  });

  const { mutateAsync, isPending } = mutation;

  const update = useCallback(
    (payload: OnboardingUpdatePayload) => mutateAsync(payload),
    [mutateAsync]
  );

  const dismissHint = useCallback(
    (key: string) => {
      // Optimistic append so the UI responds instantly. Idempotent: never
      // duplicates a key that is already present.
      setUser((prev) => {
        if (!prev) return prev;
        const hints = prev.dismissed_hints ?? [];
        if (hints.includes(key)) return prev;
        return { ...prev, dismissed_hints: [...hints, key] };
      });
      return mutateAsync({ dismiss_hint: key });
    },
    [mutateAsync, setUser]
  );

  return {
    onboardingStep: user?.onboarding_step ?? 0,
    isOnboarded: Boolean(user?.onboarding_completed_at),
    dismissedHints: user?.dismissed_hints ?? [],
    update,
    dismissHint,
    isUpdating: isPending,
  };
}
